import { useEffect, useRef, useState } from 'react'
import { TITLE_BLUE } from '../data/site.js'
import { Link } from 'react-router-dom'
import { TESTIMONIALS as DEFAULT_TESTIMONIALS } from '../pages/Home.jsx'
import { apiAsset } from '../lib/apiAsset.js'

/* ============================================================
   TESTIMONIALS — clean 2-row card grid. Top row: 3 video reviews
   (poster + play overlay, opens inline player). Bottom row: 3 text
   reviews (quote + avatar + stars). No orbit, no marquee — just
   card designs.
   ============================================================ */

/* The figure the rest of the site quotes ("Rated 4.9/5 by 33+ clients").
   The handful of excerpts on this page average 5.0, which reads as invented;
   this keeps the two places in agreement. */
const PUBLISHED_RATING = '4.9'

function StarsRow({ count = 5, size = 'sm', animated = false }) {
  const cls =
    size === 'xl' ? 'size-6 sm:size-7' : size === 'lg' ? 'size-4' : size === 'sm' ? 'size-3.5' : 'size-3'
  // role="img" — aria-label is not permitted on a bare div.
  return (
    <div
      className={`flex items-center ${size === 'xl' ? 'gap-1' : 'gap-0.5'} ${animated ? 'stars-anim' : ''}`}
      role="img"
      aria-label={`${count} out of 5 stars`}
    >
      {Array.from({ length: count }).map((_, i) => (
        <svg
          key={i}
          viewBox="0 0 20 20"
          style={animated ? { '--i': i } : undefined}
          className={`${cls} fill-amber-400`}
          aria-hidden="true"
        >
          <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.78L10 14.77 4.8 17.5l.99-5.78L1.58 7.62l5.82-.85L10 1.5z" />
        </svg>
      ))}
    </div>
  )
}

function VideoReviewCard({ t, idx = 0 }) {
  const videoRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    const onTime = () => { if (v.duration) setProgress((v.currentTime / v.duration) * 100) }
    const onEnd = () => { setPlaying(false); setProgress(0) }
    v.addEventListener('timeupdate', onTime)
    v.addEventListener('ended', onEnd)
    return () => {
      v.removeEventListener('timeupdate', onTime)
      v.removeEventListener('ended', onEnd)
    }
  }, [])

  const toggle = () => {
    const v = videoRef.current
    if (!v) return
    if (v.paused) { v.play(); setPlaying(true) }
    else { v.pause(); setPlaying(false) }
  }

  return (
    <article
      className="svc-card-overlay rv-card-in group relative flex h-full flex-col overflow-hidden rounded-[24px] border border-white/[0.08] bg-ink-950 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.7)] transition-all duration-500 hover:-translate-y-2 hover:border-cyan-400/40 hover:shadow-[0_40px_80px_-25px_rgba(34,211,238,0.25)]"
      style={{ animationDelay: `${idx * 110}ms` }}
    >
      {/* Subtle accent rim that brightens on hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ boxShadow: 'inset 0 0 0 1px rgba(103,232,249,0.18)' }}
      />

      {/* Video frame — clean, no name overlay (moved into the caption block) */}
      <div className="svc-card-overlay relative aspect-[16/10] w-full overflow-hidden bg-ink-900">
        <video
          ref={videoRef}
          src={t.video}
          poster={t.poster}
          playsInline
          preload="metadata"
          onClick={toggle}
          className={`h-full w-full cursor-pointer object-cover transition-transform duration-[1200ms] ease-out ${
            playing ? 'scale-100' : 'scale-105 group-hover:scale-[1.12]'
          }`}
        />

        {/* Cinematic gradient veil for chip legibility */}
        {!playing && (
          <>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/30 transition-opacity duration-500 group-hover:opacity-90"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute -inset-y-2 -left-1/2 w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-hover:rv-sweep"
            />
          </>
        )}

        {/* Play button — double-ring ping */}
        {!playing && (
          <button
            type="button"
            onClick={toggle}
            aria-label={`Play ${t.name}'s video review`}
            className="absolute inset-0 grid place-items-center"
          >
            <span
              className="relative inline-flex h-7 w-10 items-center justify-center rounded-[10px] bg-[#ff0000] shadow-[0_10px_24px_-8px_rgba(255,0,0,0.55)] transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_14px_32px_-8px_rgba(255,0,0,0.75)] sm:h-9 sm:w-12"
            >
              <svg viewBox="0 0 24 24" className="ml-[1px] h-3.5 w-3.5 fill-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)] sm:h-4 sm:w-4">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </button>
        )}

        {/* Stars chip — top-left */}
        <div className="absolute left-4 top-4 flex items-center gap-0.5 rounded-full bg-black/55 px-2 py-1 backdrop-blur-md">
          <StarsRow count={t.rating || 5} />
        </div>

        {/* Duration chip — top-right */}
        {t.duration && !playing && (
          <div className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 font-mono text-[11px] text-white/90 backdrop-blur-md">
            <svg viewBox="0 0 24 24" className="size-3 stroke-cyan-300" aria-hidden="true">
              <circle cx="12" cy="12" r="10" fill="none" strokeWidth="2" />
              <path d="M12 7v5l3 2" fill="none" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {t.duration}
          </div>
        )}

        {/* "Watch story →" pill that slides up on hover */}
        {!playing && (
          <span
            aria-hidden
            className="pointer-events-none absolute right-4 bottom-4 inline-flex translate-y-2 items-center gap-1.5 rounded-full bg-cyan-300 px-3 py-1.5 text-[11px] font-semibold text-ink-950 opacity-0 shadow-lg transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100"
          >
            Watch story
            <span>→</span>
          </span>
        )}

        {/* Progress bar */}
        {playing && (
          <div className="absolute inset-x-0 bottom-0 h-1 bg-white/10">
            <div className="h-full bg-cyan-300 transition-[width] duration-150" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>

      {/* Caption block — refined hierarchy:
          1. Quote (largest text, takes the focal weight)
          2. Divider
          3. Attribution: avatar + name + role/company
          4. Highlight chip on its own row to avoid wrapping/overflow
      */}
      <div className="flex flex-1 flex-col gap-5 p-6 sm:p-7">
        {/* Quote mark + body */}
        <div className="relative">
          <span
            aria-hidden
            className="absolute -left-1 -top-3 font-display text-4xl leading-none text-cyan-300/40"
          >
            “
          </span>
          <p className="pl-5 text-[15px] leading-[1.65] text-white/90">
            {t.quote}
          </p>
        </div>

        {/* Divider */}
        <span aria-hidden className="h-px w-full bg-gradient-to-r from-transparent via-white/15 to-transparent" />

        {/* Attribution row */}
        <footer className="mt-auto flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <span className="relative">
              <img
                src={t.avatar}
                alt=""
                className="size-11 flex-shrink-0 rounded-full border border-white/15 object-cover"
              />
              {/* tiny live-verified ring */}
              <span
                aria-hidden
                className="absolute -bottom-0.5 -right-0.5 grid size-4 place-items-center rounded-full bg-cyan-300 ring-2 ring-ink-950"
              >
                <svg viewBox="0 0 24 24" className="size-2.5 stroke-ink-950" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12l5 5L20 7" />
                </svg>
              </span>
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-semibold leading-tight text-white">
                {t.name}
              </p>
              <p className="mt-0.5 truncate font-mono text-[10.5px] uppercase tracking-[0.18em] text-white/55">
                {t.role} <span className="text-white/35">·</span> {t.company}
              </p>
            </div>
          </div>

          {/* Highlight chip — separate row, full width safe */}
          {t.highlight && (
            <div className="flex items-center gap-2 rounded-lg border border-cyan-300/25 bg-cyan-300/[0.07] px-3 py-2">
              <span className="font-display text-base font-bold leading-none text-cyan-200">
                {t.highlight}
              </span>
              <span className="h-3 w-px bg-cyan-300/25" />
              <span className="text-[11px] font-medium text-white/70">
                {t.highlightLabel}
              </span>
            </div>
          )}
        </footer>
      </div>
    </article>
  )
}

function TextReviewCard({ t, idx = 0 }) {
  return (
    <article
      className="svc-card-overlay rv-card-in group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-ink-950 p-6 transition-all duration-500 hover:-translate-y-1.5 hover:border-cyan-300/25 hover:shadow-[0_30px_70px_-25px_rgba(103,232,249,0.22)] sm:p-7"
      style={{ animationDelay: `${idx * 110}ms` }}
    >
      {/* Background ghost glyph */}
      <span
        aria-hidden
        className="pointer-events-none absolute -right-3 -top-10 select-none font-display text-[9rem] leading-none text-white/[0.05] transition-colors duration-300 group-hover:text-white/[0.09]"
      >
        “
      </span>

      {/* Stars + verified chip */}
      <header className="flex items-center justify-between">
        <StarsRow count={t.rating || 5} size="sm" />
        <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10px] font-medium text-white/55">
          <svg viewBox="0 0 24 24" className="size-3 fill-cyan-300" aria-hidden="true">
            <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
          </svg>
          Verified
        </span>
      </header>

      {/* Quote — slight serif feel, larger lead-in */}
      <blockquote className="relative mt-5 flex-1">
        <p
          className="text-[15.5px] leading-[1.65] text-white/85"
          style={{ fontFamily: 'ui-serif, Georgia, "Times New Roman", serif' }}
        >
          <span className="text-cyan-300/80">&ldquo;</span>
          {t.quote}
          <span className="text-cyan-300/80">&rdquo;</span>
        </p>
      </blockquote>

      {/* Footer */}
      <footer className="relative mt-6 flex items-center gap-3 border-t border-white/[0.06] pt-4">
        <span className="relative">
          <span aria-hidden className="absolute inset-0 rounded-full ring-1 ring-cyan-300/0 transition-all duration-500 group-hover:ring-cyan-300/40" />
          <img src={t.avatar} alt="" className="relative size-10 flex-shrink-0 rounded-full border border-white/10 object-cover" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-white">{t.name}</p>
          <p className="truncate text-[12px] text-white/55">{t.role} · {t.company}</p>
        </div>
        <span className="ml-auto text-[10px] uppercase tracking-[0.18em] text-white/40">{t.country}</span>
      </footer>

      {/* Cyan hairline draw on hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-0 bottom-0 h-px w-0 bg-gradient-to-r from-cyan-300/0 via-cyan-300/80 to-cyan-300/0 transition-[width] duration-[900ms] ease-out group-hover:w-full"
      />
    </article>
  )
}

function RowLabel({ children, count, rightContent }) {
  return (
    <div className="mb-5 flex items-center gap-3">
      <span aria-hidden className="size-1.5 rounded-full bg-cyan-300 shadow-[0_0_10px_rgba(103,232,249,0.8)]" />
      <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">{children}</span>
      <span className="text-[11px] font-mono text-white/35">{String(count).padStart(2, '0')}</span>
      <span aria-hidden className="h-px flex-1 bg-gradient-to-r from-white/15 via-white/[0.04] to-transparent" />
      {rightContent}
    </div>
  )
}

/* pagePath selects which set of reviews to show. Defaults to the homepage
   set so existing callers are unaffected; service pages can pass their own
   route once reviews are filed against it in the portal. */
export default function ReviewsSection({ testimonials, pagePath = 'homepage' }) {
  const [videoReviews, setVideoReviews] = useState([])
  const [textReviews, setTextReviews] = useState([])
  const [loading, setLoading] = useState(!testimonials)

  useEffect(() => {
    if (testimonials) {
      const videoList = testimonials.filter(t => t.video)
      const textList = testimonials.filter(t => !t.video)
      setVideoReviews(videoList)
      setTextReviews(textList)
      return
    }

    const API_BASE = import.meta.env.VITE_API_BASE || '/api'
    
    Promise.all([
      fetch(`${API_BASE}/get-client-review-videos.php`).then(res => res.json()),
      fetch(`${API_BASE}/get-client-reviews.php?page_path=${encodeURIComponent(pagePath)}`).then(res => res.json())
    ])
    .then(([videosData, reviewsData]) => {
      if (videosData.success && videosData.videos) {
        const mappedVideos = videosData.videos.map(t => ({
          id: t.id,
          name: t.client_name,
          role: t.client_role,
          company: t.client_company,
          country: t.client_country || '',
          quote: t.quote,
          avatar: apiAsset(t.avatar_url) || '/legacy/images/avatar/avatar-default.png',
          rating: Number(t.rating || 5),
          video: t.video_url,
          poster: t.thumbnail_url,
          duration: t.duration,
          highlight: t.highlight,
          highlightLabel: t.highlight_label,
          category: t.category
        }))
        setVideoReviews(mappedVideos)
      }
      
      if (reviewsData.success && reviewsData.reviews) {
        const mappedReviews = reviewsData.reviews.map(t => ({
          id: t.id,
          name: t.client_name,
          role: t.client_role,
          company: t.client_company,
          country: t.client_country || '',
          quote: t.quote,
          avatar: apiAsset(t.avatar_url) || '/legacy/images/avatar/avatar-default.png',
          rating: Number(t.rating || 5)
        }))
        setTextReviews(mappedReviews)
      }
      setLoading(false)
    })
    .catch(err => {
      console.error("Failed to fetch reviews:", err)
      setLoading(false)
    })
  }, [testimonials, pagePath])

  const sliderRef = useRef(null)

  const scrollLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -420, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: 420, behavior: 'smooth' })
    }
  }

  if (loading) {
    return null
  }

  if (videoReviews.length === 0 && textReviews.length === 0) {
    return null
  }

  const allReviews = [...videoReviews, ...textReviews]
  const totalCount = allReviews.length
  const avgRating = totalCount > 0 
    ? (allReviews.reduce((s, t) => s + (t.rating || 5), 0) / totalCount).toFixed(1)
    : '5.0'
  void avgRating // sample average, kept for reference; the banner quotes PUBLISHED_RATING

  return (
    <section
      aria-labelledby="reviews-heading"
      className="standalone-page relative overflow-hidden bg-ink-950 text-white"
    >
      {/* Ambient cyan glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 h-[460px] w-[820px] -translate-x-1/2 rounded-full"
        style={{
          background: 'radial-gradient(closest-side, rgba(103,232,249,0.10), rgba(103,232,249,0.04) 45%, transparent 70%)',
          filter: 'blur(2px)',
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 pt-10 pb-14 sm:pt-14 sm:pb-16 lg:pt-16 lg:px-8">
        {/* Header */}
        <header className="mx-auto mb-12 max-w-3xl text-center sm:mb-16">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs font-medium text-white/70">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inset-0 animate-ping rounded-full bg-cyan-300/60" />
              <span className="relative h-1.5 w-1.5 rounded-full bg-cyan-300" />
            </span>
            Reviews
          </span>
          <h2
            id="reviews-heading"
            className="mt-5 font-display text-4xl font-semibold leading-[1.1] text-white sm:text-5xl lg:text-[56px]"
          >
            What our customers say about{' '}
            <span style={{ color: TITLE_BLUE }}>working with us</span>
          </h2>

          {/* Stats banner */}
          <style>{`
            @keyframes rv-star-pop {
              0%   { opacity: 0; transform: scale(.2) rotate(-45deg); }
              60%  { opacity: 1; transform: scale(1.28) rotate(12deg); }
              100% { opacity: 1; transform: scale(1) rotate(0deg); }
            }
            /* Slow shimmer once the row has landed, so the badge keeps a little
               life without pulling attention off the quotes below it. */
            @keyframes rv-star-glow {
              0%, 100% { filter: drop-shadow(0 0 0 rgba(251,191,36,0)); }
              50%      { filter: drop-shadow(0 0 7px rgba(251,191,36,0.85)); }
            }
            .stars-anim svg {
              animation:
                rv-star-pop .62s cubic-bezier(.22,1.3,.36,1) both,
                rv-star-glow 2.8s ease-in-out infinite;
              animation-delay: calc(var(--i) * .11s), calc(.9s + var(--i) * .16s);
            }
            @media (prefers-reduced-motion: reduce) {
              .stars-anim svg { animation: none; opacity: 1; }
            }
          `}</style>
          <div className="mx-auto mt-7 inline-flex items-center gap-5 rounded-full border border-white/10 bg-white/[0.03] px-6 py-3 text-xs text-white/65 sm:gap-7 sm:text-sm">
            <span className="inline-flex items-center gap-2.5">
              <StarsRow count={5} size="xl" animated />
              <span className="ml-0.5 font-display text-xl font-bold leading-none text-white sm:text-2xl">
                {PUBLISHED_RATING}
              </span>
              <span className="text-white/50">avg rating</span>
            </span>
            {videoReviews.length > 0 && (
              <>
                <span aria-hidden className="hidden h-4 w-px bg-white/15 sm:block" />
                <span className="hidden items-center gap-1.5 sm:inline-flex">
                  <span className="font-semibold text-white">{videoReviews.length}</span>
                  <span className="text-white/50">on camera</span>
                </span>
              </>
            )}
          </div>
        </header>

        {/* Video row */}
        {videoReviews.length > 0 && (
          <>
            <RowLabel 
              count={videoReviews.length}
              rightContent={
                <div className="flex items-center gap-2">
                  <button
                    onClick={scrollLeft}
                    className="flex size-9 items-center justify-center rounded-full border border-white/15 bg-white/[0.03] text-white/70 transition-all hover:bg-white/[0.08] hover:text-white hover:scale-105 active:scale-95"
                    aria-label="Scroll left"
                  >
                    <svg viewBox="0 0 24 24" className="size-4 fill-none stroke-current stroke-[2]" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                  </button>
                  <button
                    onClick={scrollRight}
                    className="flex size-9 items-center justify-center rounded-full border border-white/15 bg-white/[0.03] text-white/70 transition-all hover:bg-white/[0.08] hover:text-white hover:scale-105 active:scale-95"
                    aria-label="Scroll right"
                  >
                    <svg viewBox="0 0 24 24" className="size-4 fill-none stroke-current stroke-[2]" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                  </button>
                </div>
              }
            >
              Video stories
            </RowLabel>
            
            <div 
              ref={sliderRef}
              className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-6"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {videoReviews.map((t, i) => (
                <div key={t.name || t.id} className="w-[85vw] max-w-[420px] shrink-0 snap-center">
                  <VideoReviewCard t={t} idx={i} />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Text row */}
        {textReviews.length > 0 && (
          <div className="mt-14">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {textReviews.map((t, i) => (
                <TextReviewCard key={t.name || t.id} t={t} idx={i} />
              ))}
            </div>
          </div>
        )}

        {/* Footer CTA */}
        <div className="mt-10 sm:mt-14 flex justify-center">
          <Link
            to="/portfolio"
            className="group/cta inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/[0.05] px-5 py-2.5 font-sans text-xs font-semibold uppercase tracking-wide text-white backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-300/60 hover:bg-cyan-400/10 hover:shadow-[0_0_20px_rgba(56,189,248,0.3)]"
          >
            <span aria-hidden="true" className="size-1.5 rounded-full bg-cyan-400 transition-transform duration-300 group-hover/cta:scale-125 shadow-[0_0_6px_#38bdf8]" />
            <span>See our work</span>
            <span aria-hidden="true" className="grid size-5 place-items-center rounded-full border border-white/20 transition-all duration-300 group-hover/cta:border-cyan-300/60 group-hover/cta:bg-cyan-300/20">
              <svg viewBox="0 0 24 24" className="h-3 w-3 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </span>
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes rv-card-in {
          0%   { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0);    }
        }
        .rv-card-in { animation: rv-card-in 700ms cubic-bezier(.22,.61,.36,1) both; }

        @keyframes rv-ping-1 {
          0%   { transform: scale(1);   opacity: 0.7; }
          80%  { transform: scale(1.5); opacity: 0; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes rv-ping-2 {
          0%   { transform: scale(1);   opacity: 0.5; }
          80%  { transform: scale(1.8); opacity: 0; }
          100% { transform: scale(1.8); opacity: 0; }
        }
        .rv-ping-1 { animation: rv-ping-1 2.4s ease-out infinite; }
        .rv-ping-2 { animation: rv-ping-2 2.4s ease-out 0.6s infinite; }

        @keyframes rv-sweep {
          0%   { transform: translateX(0)     skewX(-12deg); }
          100% { transform: translateX(300%)  skewX(-12deg); }
        }
        .group:hover .group-hover\\:rv-sweep { animation: rv-sweep 1.2s ease-out 1; }
        .rv-sweep { animation: rv-sweep 1.2s ease-out 1; }

        @keyframes rv-ring-slow {
          0%   { transform: scale(1);    opacity: 0.55; }
          50%  { transform: scale(1.08); opacity: 0.2; }
          100% { transform: scale(1);    opacity: 0.55; }
        }
        .rv-ring-slow { animation: rv-ring-slow 3.6s ease-in-out infinite; }
      `}</style>
    </section>
  )
}
